import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { addDays, format, isBefore, isValid, parse, parseISO, startOfDay } from "date-fns";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import { leaveStatusVisual } from "./LeaveStatusCards";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designShell,
} from "services/constants/designTokens";

const detailAccent = {
  period: detailAccentPalette.indigo,
  type: detailAccentPalette.pink,
  days: detailAccentPalette.green,
  reason: detailAccentPalette.orange,
  policy: detailAccentPalette.purple,
};

const parseKnownDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const text = String(value).trim();
  // date-fns's parse() ignores unmatched trailing text instead of failing,
  // so a date-only pattern like "dd-MM-yyyy" against a full timestamp (the
  // approved_on/rejected_on shape the backend sends: "16-09-2026, 14:30")
  // still "succeeds" — but silently backfills the missing hour/minute from
  // referenceDate (i.e. the current time), showing the wrong time. The
  // timestamp formats must be tried first so they win the match.
  const knownFormats = [
    "dd-MM-yyyy, HH:mm",
    "dd-MM-yyyy HH:mm",
    "dd-MM-yyyy",
    "yyyy-MM-dd",
    "dd/MM/yyyy",
    "yyyy/MM/dd",
  ];

  for (const pattern of knownFormats) {
    const parsed = parse(text, pattern, new Date());
    if (isValid(parsed)) return parsed;
  }

  const parsedIso = parseISO(text);
  return isValid(parsedIso) ? parsedIso : null;
};

const formatDisplayDate = (value) => {
  const parsed = parseKnownDate(value);
  return parsed ? format(parsed, "dd MMM yyyy") : value || "-";
};

// Only appends the time portion when the raw value actually carries one
// (e.g. a full "applied_on" timestamp) — a plain date-only value (like the
// legacy "applied_date" field) would otherwise default to midnight and
// misleadingly render as "12:00 AM".
const formatDisplayDateTime = (value) => {
  const parsed = parseKnownDate(value);
  if (!parsed) return value || "-";
  const hasTime = /\d{1,2}:\d{2}/.test(String(value));
  return format(parsed, hasTime ? "dd MMM yyyy, hh:mm a" : "dd MMM yyyy");
};

const hasLeavePeriodEnded = (toDateValue) => {
  const endDate = parseKnownDate(toDateValue);
  return endDate ? isBefore(startOfDay(endDate), startOfDay(new Date())) : false;
};

// Cancelling from the Approve side always cancels the whole approved leave
// in one action (there's no partial-date picker here, unlike MyZone's own
// employee-facing CancelLeaveDialog) — so this just enumerates every date in
// the original range for the cancel_create_leave_request/ payload's
// invalid_dates field.
const buildFullLeaveDateList = (fromDateValue, toDateValue) => {
  const start = parseKnownDate(fromDateValue);
  const end = parseKnownDate(toDateValue) || start;
  if (!start || !end) return [];
  const dates = [];
  let current = start;
  while (current.getTime() <= end.getTime()) {
    dates.push(format(current, "yyyy-MM-dd"));
    current = addDays(current, 1);
  }
  return dates;
};

const formatDayCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  const formatted = Number.isInteger(numeric) ? `${numeric}` : `${numeric}`;
  return `${formatted} ${numeric <= 1 ? "Day" : "Days"}`;
};

const formatHalfDayLabel = (leave) => {
  const raw =
    leave?.which_half || leave?.half_day_period || leave?.half_day_type;
  if (!raw) return "";
  const text = String(raw).trim();
  if (!text) return "";
  return /half/i.test(text) ? text : `${text} Half`;
};

const getRequestReason = (leave) =>
  leave?.applied_reason || leave?.description || leave?.reason || "-";

export const LeaveDetailPanel = ({
  open,
  onClose,
  leave,
  checkAccess,
  setReCallApi,
  leaveYearId,
  expanded = false,
  onToggleExpand,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  // Reuses the same cancel_create_leave_request/ endpoint MyZone's own
  // CancelLeaveDialog (pages/TeamZone/LeaveSummary/CancelLeave.js) calls —
  // that dialog lets an employee cancel only part of their own leave's
  // dates via a picker; here the approver cancels the whole approved leave
  // in one action instead, through the same simple reason-only popup every
  // other Approve screen's cancel uses, per user request.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { domain, token, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // checkAccess (prop, from Leave/index.js) is plain editor permission — it
  // used to gate Approve/Reject/Cancel here too, which meant any editor
  // could approve, reject or cancel someone else's leave even with no
  // approver permission at all. Those three actions (the only things this
  // panel gates) now require real approver permission instead (either
  // level counts — see Leave/Apply.js for the fuller history of this same
  // distinction on the create side). checkAccess itself is left as a prop
  // here rather than removed, since Leave/index.js still passes it and a
  // future plain edit action in this panel (as opposed to an approval
  // decision) would reach for it again.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Leaves",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Leaves",
    );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: { rejected_reason: "" },
  });

  useEffect(() => {
    setRejectDialogOpen(false);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    reset({ rejected_reason: "" });
  }, [leave, reset]);

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    reset({ rejected_reason: "" });
    clearErrors("rejected_reason");
  };

  useEffect(() => {
    register("rejected_reason", {
      required: "Reject reason is required",
      validate: {
        minLengthNoSpaces: (value) => {
          if (value?.trim().length < 3) return minimum3Characters;
          if (value?.trim().length > 255) {
            return "Maximum length is 255 characters";
          }
          return true;
        },
        atleastOneCharacterWithSpaceReg: (value) =>
          atleastOneCharacterWithSpace.test(value) ||
          "Atleast one alphabet required",
      },
    });
  }, [register]);

  const statusFlowData = useMemo(() => {
    if (!leave) return null;
    return {
      ...leave,
      applied_by:
        leave?.applied_by ||
        leave?.employee_name ||
        leave?.username ||
        leave?.requested_by,
      applied_on: formatDisplayDateTime(
        leave?.applied_on ||
          leave?.applied_date ||
          leave?.created_date ||
          leave?.requested_date ||
          leave?.created_at,
      ),
      applied_reason: getRequestReason(leave),
      // leave?.approver_name is the assigned approver, stamped at apply
      // time before anyone has acted -- it is not proof of approval and
      // must not be preferred over (or substitute for) the backend's
      // status-derived approved_by.
      approved_by: leave?.approved_by,
      approved_at: formatDisplayDateTime(
        leave?.approved_on || leave?.approved_at,
      ),
    };
  }, [leave]);

  if (!leave || !statusFlowData) return null;

  const status = String(leave?.status || "");
  const isPending = status.toLowerCase() === "pending";
  const employeeName = leave?.employee_name || leave?.username || "N/A";
  const employeeId = leave?.employee_id || leave?.emp_id || "-";
  const department = leave?.department_name || leave?.department || "-";
  const designation = leave?.designation_name || leave?.designation || "-";
  const dayLabel = formatDayCount(leave?.business_days);
  const halfDayLabel = leave?.is_half_day ? formatHalfDayLabel(leave) : "";
  const periodHelper = [dayLabel !== "-" ? dayLabel : "", halfDayLabel]
    .filter(Boolean)
    .join(" / ");
  const leavePeriod = leave?.from_date
    ? `${formatDisplayDate(leave.from_date)} - ${formatDisplayDate(leave?.to_date)}`
    : leave?.requested_date
      ? formatDisplayDate(leave?.requested_date)
      : "-";
  const requestReason = getRequestReason(leave);
  const isLeaveCompleted =
    status.toLowerCase() === "approved" && hasLeavePeriodEnded(leave?.to_date);
  // Requested per user: allow cancelling an approved leave the same way
  // Comp Off does — including once it's been marked Completed (the leave
  // period has already ended), not just while it's still ongoing.
  const canCancelApproved = status.toLowerCase() === "approved";

  const handleCloseAction = () => {
    onClose?.();
  };

  const updateLeaveStatus = async (requestStatus, reason) => {
    try {
      const response = await fetch(`${domain}leave_request_update/?company_id=${requestCompanyId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          id: leave.id,
          approver_id: userDetails.id,
          request_status: requestStatus,
          approver_name: userDetails.first_name,
          ...(requestStatus === "Rejected" && { reason }),
        }),
      });
      const res = await response.json();
      if (response.ok) {
        showToast(
          requestStatus === "Approved" ? "success" : "error",
          res.message,
        );
        setReCallApi?.(true);
        setRejectDialogOpen(false);
        onClose?.();
        return true;
      }
      if (response.status === 409) {
        sessionHandling();
        return false;
      }
      throw new Error("REQUEST_FAILED");
    } catch (error) {
      console.error("[LeaveDetailPanel] operation failed:", error);
      showToast(
        "error",
        `Couldn't ${requestStatus === "Approved" ? "approve" : "decline"} the leave request. Please try again.`,
      );
      return false;
    }
  };

  const handleApprove = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this leave request",
      );
      return;
    }
    setApproving(true);
    await updateLeaveStatus("Approved");
    setApproving(false);
  };

  const submitReject = async (data) => {
    // The Reject button itself is disabled without approver access (see
    // footerContent below), but this backstop mirrors handleApprove/
    // submitCancel in case the reject dialog is ever reachable some other
    // way.
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this leave request",
      );
      return;
    }
    setRejecting(true);
    await updateLeaveStatus("Rejected", data?.rejected_reason);
    setRejecting(false);
  };

  const submitCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this leave request",
      );
      return;
    }
    setCancelling(true);
    const validation = await customGetAPI(
      `${domain}valid_business_days/?leave_request_id=${leave.id}`,
    );
    const dateValidation = validation?.responseDetails?.date_validation || {};
    const invalidDates = buildFullLeaveDateList(leave?.from_date, leave?.to_date);
    const response = await customAPI(
      `${domain}cancel_create_leave_request/`,
      "PUT",
      {
        leave_request_id: leave.id,
        date_validation: dateValidation,
        invalid_dates: invalidDates,
        leave_year_id: leaveYearId,
        reason: trimmed,
      },
    );
    setCancelling(false);
    if (response?.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Leave request cancelled.",
      );
      setReCallApi?.(true);
      setCancelDialogOpen(false);
      onClose?.();
    } else {
      showToast(
        "error",
        response?.message || "Couldn't cancel the leave request. Please try again.",
      );
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  // Reject/Approve/Cancel below used to always render, only disabled (via
  // AccessGatedAction) when hasApproverAccess was false — leaving a
  // bordered footer strip with unusable buttons for an editor-only viewer.
  // Each branch now resolves to real content or a genuine `null` (same fix
  // as OverTime/DetailPanel.js) so GlobalSidePanel's own footer border only
  // draws when there's actually something to show.
  const footerContent = isPending ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5} sx={{ width: "100%" }}>
        <Mui.Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: "100%" }}
        >
          <Mui.Button
            variant="outlined"
            onClick={() => setRejectDialogOpen(true)}
            startIcon={<HighlightOffIcon sx={{ fontSize: 17 }} />}
            disabled={rejecting || approving}
            sx={{
              ...designShell.panelActionButton,
              flex: 1,
              minWidth: 0,
              minHeight: 42,
              color: "#E11D48",
              borderColor: "#FDA4AF",
              backgroundColor: designColors.white,
              "&:hover": {
                color: "#BE123C",
                borderColor: "#FB7185",
                backgroundColor: "#FFF1F3",
              },
            }}
          >
            Reject
          </Mui.Button>
          <PrimaryButton
            onClick={handleApprove}
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 17 }} />}
            disabled={rejecting}
            loading={approving}
            sx={{ flex: 1, minWidth: 0, minHeight: 42 }}
          >
            Approve Request
          </PrimaryButton>
        </Mui.Stack>
      </Mui.Stack>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5} sx={{ width: "100%" }}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          {isLeaveCompleted
            ? "This leave has been completed."
            : "This request has already been approved."}
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Leave
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : null;

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Leave Request"
      onCancel={closeRejectDialog}
         onConfirm={() => {
        if (!watch("rejected_reason")?.trim()) {
          setError("rejected_reason", {
            type: "required",
            message: "Reject reason is required",
          });
          return;
        }
        handleSubmit(submitReject)();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={rejecting}
      danger
      reasonValue={watch("rejected_reason") || ""}
      onReasonChange={(value) =>
        setValue("rejected_reason", value, { shouldValidate: true })
      }
      reasonError={errors?.rejected_reason?.message || ""}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this leave request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Leave Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={cancelling}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved leave"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This leave request has already been approved. Enter a reason to
        cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Leave Request"
        subtitle="Request details and status"
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Leave", onClick: handleCloseAction },
          employeeName || "Leave Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: leave?.image_url,
              employee_id: employeeId,
              user_name: employeeName,
              department_name: department,
              designation,
            }}
            status={status}
            colorCode={leave?.color_code || leaveStatusVisual(status).accent}
          />

          <Mui.Box sx={detailCardSx}>
            <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
              Request Details
            </Mui.Typography>
            <RequestDetailField
              icon={CalendarMonthIcon}
              label="Leave Period"
              value={leavePeriod}
              helper={periodHelper}
              accent={detailAccent.period}
            />
            <RequestDetailField
              icon={AccountBalanceWalletOutlinedIcon}
              label="Leave Type"
              value={leave?.leave_type || "-"}
              accent={detailAccent.type}
            />
            <RequestDetailField
              icon={EventAvailableOutlinedIcon}
              label="Business Days"
              value={dayLabel}
              rightPill={halfDayLabel}
              accent={detailAccent.days}
            />
            <RequestDetailField
              icon={DescriptionOutlinedIcon}
              label="Reason"
              value={requestReason}
              accent={detailAccent.reason}
              multiline
              isLast={!(leave?.is_sandwich_applied && leave?.comments)}
            />
            {leave?.is_sandwich_applied && leave?.comments ? (
              <RequestDetailField
                icon={DescriptionOutlinedIcon}
                label="Sandwich Policy"
                value={leave.comments}
                accent={detailAccent.policy}
                multiline
                isLast
              />
            ) : null}
          </Mui.Box>

          <RequestStatusTimeline
            data={statusFlowData}
            variant="stepper"
            forceCompleted={isLeaveCompleted}
            hidePendingStep
          />
          <ApprovalEditAuditTrail data={leave} />

          {status.toLowerCase() === "approved" && !isLeaveCompleted ? (
            <RequestInfoBanner
              title="This request will be marked as Completed after the leave period ends."
              body="For single day leave, it will be completed at the end of that day."
            />
          ) : null}
        </Mui.Box>
      </GlobalSidePanel>
      {rejectDialog}
      {cancelDialog}
    </>
  );
};
